import React, { useState } from "react";
import "./BookTable.css";
import { Helmet } from "react-helmet";

function BookTablePage() {
  const [isLoading, setIsLoading] = useState(false);
  const [dateType, setDateType] = useState("text");
  const [formData, setFormData] = useState({
    seats: "",
    date: "",
    time: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    specialRequest: "",
  });

  const generateTimeOptions = () => {
    const options = [];
    const startHour = 13;
    const endHour = 21;

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minutes = 0; minutes < 60; minutes += 15) {
        const timeString = new Date(0, 0, 0, hour, minutes).toLocaleTimeString(
          [],
          { hour: "2-digit", minute: "2-digit", hour12: true },
        );
        options.push(timeString);
      }
    }
    return options;
  };

  const handleDateFocus = () => setDateType("date");
  const handleDateBlur = (e) => {
    if (!e.target.value) {
      setDateType("text");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // 1️⃣ Save booking in database
      const response = await fetch(
        "https://lasgidirestaurant.com/php/book-table.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        },
      );

      const result = await response.json();

      if (!result.success) {
        alert("Failed to save booking: " + result.message);
        setIsLoading(false);
        return;
      }

      // 2️⃣ Construct WhatsApp message
      const message =
        `*New Table Booking*\n\n` +
        `*Name:* ${formData.firstName} ${formData.lastName}\n` +
        `*Phone:* ${formData.phone}\n` +
        `*Email:* ${formData.email}\n` +
        `*Seats:* ${formData.seats}\n` +
        `*Date:* ${formData.date}\n` +
        `*Time:* ${formData.time}\n` +
        `*Special Request:* ${formData.specialRequest || "None"}`;

      const encodedMessage = encodeURIComponent(message);
      const whatsappURL = `https://wa.me/447368325136?text=${encodedMessage}`;

      // 3️⃣ Open WhatsApp
      window.open(whatsappURL, "_blank");

      // 4️⃣ Optionally reset form
      setFormData({
        seats: "",
        date: "",
        time: "",
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        specialRequest: "",
      });
    } catch (error) {
      console.error(error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const SpinnerOverlay = () => (
    <div className="spinner-overlay">
      <div className="spinner"></div>
    </div>
  );

  return (
    <div className="booktable-container page-container">
      <Helmet>
        <title>Book a Table | LasGidi Restaurant</title>
        <meta
          name="description"
          content="Reserve a table at LasGidi Restaurant in Manchester and enjoy authentic Nigerian cuisine with your friends and family."
        />
        <meta
          name="keywords"
          content="LasGidi Restaurant, Book Table, Nigerian Cuisine, Manchester Restaurant, Table Reservation"
        />
        <meta property="og:title" content="Book a Table | LasGidi Restaurant" />
        <meta
          property="og:description"
          content="Reserve a table at LasGidi Restaurant in Manchester and enjoy authentic Nigerian cuisine with your friends and family."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      {isLoading && <SpinnerOverlay />}
      <div className="booking-container heading-container">
        <h1>Book a Table</h1>
      </div>
      <form className="booktable-form" onSubmit={handleSubmit}>
        <h2>Reserve your table now for an unforgettable dining experience!</h2>
        <div className="form-fields">
          <div className="form-group">
            <input
              type="number"
              id="seats"
              min="1"
              name="seats"
              value={formData.seats}
              onChange={handleChange}
              placeholder="Number of seats"
              required
            />
          </div>

          <div className="form-group">
            <input
              type={dateType}
              id="date"
              name="date"
              value={formData.date}
              min={new Date().toISOString().split("T")[0]}
              onChange={handleChange}
              placeholder="Select Date"
              onFocus={handleDateFocus}
              onBlur={handleDateBlur}
              required
            />
          </div>

          <div className="form-group">
            <select
              id="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              style={{ color: formData.time === "" ? "#777777" : "#000" }}
              required
            >
              <option value="">Select time</option>
              {generateTimeOptions().map((timeOption, index) => (
                <option key={index} value={timeOption}>
                  {timeOption}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First name"
              required
            />
          </div>

          <div className="form-group">
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last name"
              required
            />
          </div>

          <div className="form-group">
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone number"
              required
            />
          </div>

          <div className="form-group">
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email address"
              required
            />
          </div>

          <div className="form-group">
            <textarea
              id="specialRequest"
              name="specialRequest"
              value={formData.specialRequest}
              onChange={handleChange}
              placeholder="Special request"
              rows="4"
            ></textarea>
          </div>
        </div>
        <button
          type="submit"
          className="cta-button primary-btn post-review-btn book-btn"
          disabled={isLoading}
        >
          {isLoading ? "Booking..." : "Book Now"}
        </button>
      </form>
    </div>
  );
}

export default BookTablePage;
